import { z } from 'zod';

// OrganizationMenuCategories schemas
export const organizationMenuCategoriesSearchRequestSchema = z.object({
  name: z.string().optional(),
  status: z.enum(['Passive', 'Active']).optional(),
  pageNumber: z.number().optional(),
  pageSize: z.number().optional(),
  OrderColumn: z.string().optional(),
  OrderType: z.string().optional(),
});

export const organizationMenuCategoryItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(['Passive', 'Active']),
});

export const organizationMenuCategoriesSearchResponseSchema = z.object({
  totalCount: z.number(),
  result: z.array(organizationMenuCategoryItemSchema),
});

export type OrganizationMenuCategoriesSearchRequest = z.infer<typeof organizationMenuCategoriesSearchRequestSchema>;
export type OrganizationMenuCategoryItem = z.infer<typeof organizationMenuCategoryItemSchema>;
export type OrganizationMenuCategoriesSearchResponse = z.infer<typeof organizationMenuCategoriesSearchResponseSchema>;

// OrganizationMenuCategories Dropdown schemas
export const organizationMenuCategoryDropdownItemSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const organizationMenuCategoriesDropdownResponseSchema = z.array(organizationMenuCategoryDropdownItemSchema);

export type OrganizationMenuCategoryDropdownItem = z.infer<typeof organizationMenuCategoryDropdownItemSchema>;
export type OrganizationMenuCategoriesDropdownResponse = z.infer<typeof organizationMenuCategoriesDropdownResponseSchema>;

export const createOrganizationMenuCategoryRequestSchema = z.object({
  name: z.record(z.string(), z.string()), // Multi-language name object
  status: z.enum(['Passive', 'Active']),
});

export const createOrganizationMenuCategoryResponseSchema = z.any();

export type CreateOrganizationMenuCategoryRequest = z.infer<typeof createOrganizationMenuCategoryRequestSchema>;
export type CreateOrganizationMenuCategoryResponse = z.infer<typeof createOrganizationMenuCategoryResponseSchema>;

export const organizationMenuCategoryDetailResponseSchema = z.object({
  id: z.string(),
  name: z.record(z.string(), z.string()), // Multi-language name object
  status: z.enum(['Passive', 'Active']),
});

export type OrganizationMenuCategoryDetailResponse = z.infer<typeof organizationMenuCategoryDetailResponseSchema>;

export const updateOrganizationMenuCategoryRequestSchema = z.object({
  name: z.record(z.string(), z.string()), // Multi-language name object
  status: z.enum(['Passive', 'Active']),
});

export const updateOrganizationMenuCategoryResponseSchema = z.any();

export type UpdateOrganizationMenuCategoryRequest = z.infer<typeof updateOrganizationMenuCategoryRequestSchema>;
export type UpdateOrganizationMenuCategoryResponse = z.infer<typeof updateOrganizationMenuCategoryResponseSchema>;

export const deleteOrganizationMenuCategoryResponseSchema = z.any();

export type DeleteOrganizationMenuCategoryResponse = z.infer<typeof deleteOrganizationMenuCategoryResponseSchema>;

// OrganizationMenus schemas
export const createOrganizationMenuRequestSchema = z.object({
  type: z.string(),
});

export const createOrganizationMenuResponseSchema = z.any();

export type CreateOrganizationMenuRequest = z.infer<typeof createOrganizationMenuRequestSchema>;
export type CreateOrganizationMenuResponse = z.infer<typeof createOrganizationMenuResponseSchema>;

export const organizationMenuItemSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.enum(['Passive', 'Active']),
  externalUrl: z.string().nullable(),
});

export const organizationMenusListResponseSchema = z.array(organizationMenuItemSchema);

export type OrganizationMenuItem = z.infer<typeof organizationMenuItemSchema>;
export type OrganizationMenusListResponse = z.infer<typeof organizationMenusListResponseSchema>;

export const updateOrganizationMenuRequestSchema = z.object({
  status: z.enum(['Passive', 'Active']),
  externalUrl: z.string().nullable(),
});

export const updateOrganizationMenuResponseSchema = z.any();

export type UpdateOrganizationMenuRequest = z.infer<typeof updateOrganizationMenuRequestSchema>;
export type UpdateOrganizationMenuResponse = z.infer<typeof updateOrganizationMenuResponseSchema>;

// OrganizationMenuProducts schemas
export const organizationMenuProductCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const organizationMenuProductItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number().nullable(),
  imageUrl: z.string(),
  category: organizationMenuProductCategorySchema.nullable(),
  status: z.enum(['Passive', 'Active']),
});

export const organizationMenuProductsSearchRequestSchema = z.object({
  Name: z.string().optional(),
  Status: z.enum(['Passive', 'Active']).optional(),
  CategoryId: z.string().optional(),
  OrganizationMenuId: z.string().optional(),
  MinPrice: z.number().optional(),
  MaxPrice: z.number().optional(),
  pageNumber: z.number().optional(),
  pageSize: z.number().optional(),
  OrderColumn: z.string().optional(),
  OrderType: z.string().optional(),
});

export const organizationMenuProductsSearchResponseSchema = z.object({
  totalCount: z.number(),
  result: z.array(organizationMenuProductItemSchema),
});

export type OrganizationMenuProductCategory = z.infer<typeof organizationMenuProductCategorySchema>;
export type OrganizationMenuProductItem = z.infer<typeof organizationMenuProductItemSchema>;
export type OrganizationMenuProductsSearchRequest = z.infer<typeof organizationMenuProductsSearchRequestSchema>;
export type OrganizationMenuProductsSearchResponse = z.infer<typeof organizationMenuProductsSearchResponseSchema>;

export const organizationMenuProductImageDocumentSchema = z.object({
  id: z.number(),
  publicUrl: z.string(),
  environmentType: z.string(),
});

export const organizationMenuProductImageUrlSchema = z.object({
  languageId: z.string(),
  documents: z.array(organizationMenuProductImageDocumentSchema),
});

export const organizationMenuProductMenuSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.enum(['Passive', 'Active']),
});

export const organizationMenuProductDetailResponseSchema = z.object({
  id: z.string(),
  name: z.record(z.string(), z.string()), // Multi-language name object
  price: z.number().nullable(),
  status: z.enum(['Passive', 'Active']),
  category: organizationMenuProductCategorySchema.nullable(),
  menu: organizationMenuProductMenuSchema,
  imageUrl: z.array(organizationMenuProductImageUrlSchema),
});

export type OrganizationMenuProductImageDocument = z.infer<typeof organizationMenuProductImageDocumentSchema>;
export type OrganizationMenuProductImageUrl = z.infer<typeof organizationMenuProductImageUrlSchema>;
export type OrganizationMenuProductMenu = z.infer<typeof organizationMenuProductMenuSchema>;
export type OrganizationMenuProductDetailResponse = z.infer<typeof organizationMenuProductDetailResponseSchema>;

export const organizationMenuProductCreateImageSchema = z.object({
  id: z.number().optional(),
  languageId: z.string(),
  document: z.any(), // File object or string
  environmentType: z.enum(['Mobile', 'Desktop']),
  IsDeleted: z.boolean().optional(),
});

export const createOrganizationMenuProductRequestSchema = z.object({
  name: z.record(z.string(), z.string()), // Multi-language name object
  price: z.number().nullable(),
  menuId: z.string(),
  categoryId: z.string(),
  status: z.enum(['Passive', 'Active']),
  image: z.array(organizationMenuProductCreateImageSchema).optional(),
});

export const createOrganizationMenuProductResponseSchema = z.any();

export type OrganizationMenuProductCreateImage = z.infer<typeof organizationMenuProductCreateImageSchema>;
export type CreateOrganizationMenuProductRequest = z.infer<typeof createOrganizationMenuProductRequestSchema>;
export type CreateOrganizationMenuProductResponse = z.infer<typeof createOrganizationMenuProductResponseSchema>;

// OrganizationMenuProduct Update schemas
export const updateOrganizationMenuProductRequestSchema = z.object({
  name: z.record(z.string(), z.string()), // Multi-language name object
  price: z.number().nullable(),
  menuId: z.string(),
  categoryId: z.string(),
  status: z.enum(['Passive', 'Active']),
  image: z.array(organizationMenuProductCreateImageSchema).optional(),
});

export const updateOrganizationMenuProductResponseSchema = z.any();

export type UpdateOrganizationMenuProductRequest = z.infer<typeof updateOrganizationMenuProductRequestSchema>;
export type UpdateOrganizationMenuProductResponse = z.infer<typeof updateOrganizationMenuProductResponseSchema>;

export const deleteOrganizationMenuProductResponseSchema = z.any();

export type DeleteOrganizationMenuProductResponse = z.infer<typeof deleteOrganizationMenuProductResponseSchema>;

